import { Component, ElementRef, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

import { ConfigService } from 'app/app-config.service';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { UserClaims } from 'app/@types/dto/user-claims.model';
import { Select } from '@ngxs/store';
import { UserState } from '@state/user.state';
import { Observable } from 'rxjs';
import { LocalStorageKeys, StorageService } from '@services/storage/storage.service';
import { CustomTranslateService } from '@services/common/custom-translate.service';
import { revealFormErrors } from 'app/main/helpers/form-validation';

@Component({
    selector: 'app-need-help-modal',
    templateUrl: './need-help-modal.component.html',
    styleUrls: ['./need-help-modal.component.scss'],
    standalone: false,
})
export class NeedHelpModalComponent implements OnInit {
    form: FormGroup;
    @Select(UserState.getCurrentUserClaims) userClaims$!: Observable<UserClaims | undefined>;
    userClaims: UserClaims | undefined;

    public partnership: boolean = false;

    constructor(
        public configService: ConfigService,
        private dialogRef: DynamicDialogRef,
        private fb: FormBuilder,
        private storageService: StorageService,
        private readonly translateService: CustomTranslateService,
        private readonly host: ElementRef<HTMLElement>,
    ) {
        this.form = this.fb.group({
            subject: ['', Validators.required],
            description: ['', Validators.required],
        });
    }
    public currentConfig: string = '';
    public currentSection: string = '';
    public backgroundUrl: string = '';
    private bccEmail: string = '';
    public local: string = 'en';

    helpButtons = [
        {
            icon: 'developer_guide',
            titleKey: 'menu.modals.needHelp.userGuide.title',
            descriptionKey: 'menu.modals.needHelp.userGuide.description',
            section: 'userGuide',
            disabled: false,
        },
        {
            icon: 'help',
            titleKey: 'menu.modals.needHelp.faq.title',
            descriptionKey: 'menu.modals.needHelp.faq.description',
            section: 'faq',
            disabled: false,
        },
        {
            icon: 'demography',
            titleKey: 'menu.modals.needHelp.contact.title',
            descriptionKey: 'menu.modals.needHelp.contact.description',
            section: 'contactDCarbonize',
            disabled: false,
        },
        {
            icon: 'content_paste_search',
            titleKey: 'menu.modals.needHelp.methode.title',
            descriptionKey: 'menu.modals.needHelp.methode.description',
            section: 'methode',
            disabled: false,
        },
    ];
    helpButtons_client = [
        {
            icon: 'developer_guide',
            titleKey: 'menu.modals.needHelp.userGuide.title',
            descriptionKey: 'menu.modals.needHelp.userGuide.description',
            section: 'userGuide',
            disabled: false,
        },
        {
            icon: 'help',
            titleKey: 'menu.modals.needHelp.faq.title',
            descriptionKey: 'menu.modals.needHelp.faq.description',
            section: 'faq',
            disabled: false,
        },
        {
            icon: 'account_balance',
            titleKey: 'menu.modals.needHelp.contact.client.title',
            descriptionKey: 'menu.modals.needHelp.contact.client.description',
            section: 'contactBanker',
            disabled: false,
        },
        {
            icon: 'demography',
            titleKey: 'menu.modals.needHelp.contact.title',
            descriptionKey: 'menu.modals.needHelp.contact.description',
            section: 'contactDCarbonize',
            disabled: false,
        },
        {
            icon: 'content_paste_search',
            titleKey: 'menu.modals.needHelp.methode.title',
            descriptionKey: 'menu.modals.needHelp.methode.description',
            section: 'methode',
            disabled: false,
        },
    ];

    ngOnInit() {
        this.userClaims$.subscribe((claims) => {
            this.userClaims = claims;
            this.local =
                this.storageService.getGlobalItem<string>(LocalStorageKeys.localeUser) ??
                this.configService.getConfig.defaultLocale;
            const clientUrl = `assets/images/background/background-needHelp.webp`;
            this.setBackground(clientUrl);
            this.currentConfig = this.configService.getClient.name === 'Dcarbonize' ? 'd-carbonize' : 'client';
            this.currentSection =
                this.configService.getClient.name === 'Dcarbonize'
                    ? this.helpButtons[0].section
                    : this.helpButtons_client[0].section;
        });

        this.partnership = this.configService.getClient.support?.isPartner ?? false;
    }

    async setBackground(url: string) {
        const exist = await this.checkUrl(url);
        this.backgroundUrl = exist ? url : 'assets/images/background/background-needHelp.webp';
    }

    async checkUrl(url: string): Promise<boolean> {
        try {
            const response = await fetch(url, { method: 'HEAD' });
            return response.ok;
        } catch (_error) {
            return false;
        }
    }

    public sendEmail(to?: string) {
        if (!revealFormErrors(this.form, this.host.nativeElement)) {
            return;
        }
        const formData = this.form.value as { subject: string; description: string };
        const clientMail: string | undefined =
            this.configService.getClient.support?.mailClient ??
            this.configService.getClient.support?.mailClientFallback;
        const mailTo =
            to === 'dcarbonize'
                ? this.configService.getClient.support?.mailDcarbonize
                : clientMail
                  ? clientMail
                  : this.userClaims?.admin_email;
        this.bccEmail = this.configService.getClient.clientConfig && mailTo === this.userClaims?.admin_email ? '' : '';

        const mail = `mailto:${mailTo}?&bcc=${this.bccEmail}&subject=${formData.subject}&body=${formData.description}`;

        window.open(mail, 'emailWindow');

        this.dialogRef.close();
    }

    public isEven(index: number): boolean {
        return index % 2 === 0;
    }

    public showSection(section: string) {
        this.currentSection = section;
    }

    async downloadFile(href: string, fallback?: string): Promise<void> {
        const exists = await this.checkUrl(href);
        const resolvedHref = exists ? href : (fallback ?? href);

        const filename = resolvedHref.split('/').pop() ?? '';
        if (!filename) {
            console.error('Invalid download path, filename could not be extracted:', resolvedHref);
            return;
        }
        const link = document.createElement('a');
        link.href = resolvedHref;
        link.download = filename.split('.')[0];
        link.click();
    }
}
